import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { UserRole } from "@/lib/types";
import logo from "@/assets/logo.png";
import Icon, { type IconName } from "./Icon";

interface NavItem {
  to: string;
  label: string;
  allow: UserRole[];
  icon: IconName;
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: "dashboard", allow: ["client", "internal", "verifier", "buyer"] },
      { to: "/map", label: "Map", icon: "map", allow: ["client", "internal", "verifier"] },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/leases", label: "Leases", icon: "leases", allow: ["client", "internal", "verifier"] },
      { to: "/entities", label: "Entities", icon: "entities", allow: ["internal"] },
      { to: "/users", label: "User Management", icon: "users", allow: ["internal"] },
      { to: "/field-activity", label: "Field activity", icon: "field", allow: ["internal"] },
      { to: "/file-share", label: "File share", icon: "share", allow: ["internal"] },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/sampling", label: "Sampling", icon: "sampling", allow: ["internal", "verifier"] },
      { to: "/analysis", label: "Analysis", icon: "analysis", allow: ["internal"] },
      { to: "/bedding", label: "Bedding claims", icon: "bedding", allow: ["internal"] },
      { to: "/check-deliveries", label: "Check deliveries", icon: "check", allow: ["internal"] },
    ],
  },
  {
    label: "Review",
    items: [
      { to: "/verification", label: "Verification", icon: "verification", allow: ["internal", "verifier"], end: true },
      { to: "/submittals", label: "Submittals", icon: "submittals", allow: ["internal", "verifier"], end: true },
      { to: "/w9-review", label: "W-9 review", icon: "tax", allow: ["internal"], end: true },
    ],
  },
  {
    label: "Reporting",
    items: [
      { to: "/tax-forms", label: "Tax forms", icon: "tax", allow: ["client"] },
      { to: "/annual-monitoring", label: "Annual monitoring", icon: "monitoring", allow: ["client", "internal"] },
      { to: "/mortality-report", label: "Mortality reporting", icon: "mortality", allow: ["client", "internal"] },
      { to: "/bedding-request", label: "Bedding reimbursement", icon: "bedding", allow: ["client"] },
      { to: "/lease-transfer", label: "Lease transfer", icon: "leases", allow: ["client"] },
    ],
  },
  {
    label: "Registry",
    items: [
      { to: "/credits", label: "Carbon credits", icon: "credits", allow: ["internal"] },
      { to: "/evidence", label: "Buyer evidence", icon: "submittals", allow: ["internal"] },
    ],
  },
  {
    label: "Marketplace",
    items: [
      { to: "/buy", label: "Buy credits", icon: "credits", allow: ["buyer"] },
      { to: "/orders", label: "Orders", icon: "submittals", allow: ["buyer"] },
      { to: "/portfolio", label: "My credits", icon: "monitoring", allow: ["buyer"] },
      { to: "/evidence", label: "Quality & evidence", icon: "verification", allow: ["buyer"] },
    ],
  },
  {
    label: "Team",
    items: [
      { to: "/timekeeping", label: "Time & expenses", icon: "time", allow: ["internal"] },
    ],
  },
];

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onMobileClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mobileOpen, onMobileClose]);

  return (
    <>
      <aside className="sidebar-shell relative hidden h-full w-(--spacing-sidebar) shrink-0 flex-col md:flex" aria-label="Primary navigation">
        <SidebarContents onNavigate={onMobileClose} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button type="button" className="mobile-nav-backdrop absolute inset-0 bg-[#06141d]/55 backdrop-blur-[2px]"
            aria-label="Close navigation" onClick={onMobileClose} />
          <aside className="sidebar-shell mobile-nav-panel relative flex h-full w-[17rem] max-w-[86vw] flex-col shadow-2xl"
            aria-label="Primary navigation">
            <SidebarContents onNavigate={onMobileClose} />
          </aside>
        </div>
      )}
    </>
  );
}

function SidebarContents({ onNavigate }: { onNavigate: () => void }) {
  const { role } = useAuth();
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => role && item.allow.includes(role)),
  })).filter((group) => group.items.length > 0);

  return (
    <>
      <div className="sidebar-atmosphere" aria-hidden />
      <div className="relative px-5 pb-5 pt-6">
        <div className="sidebar-logo-wrap">
          <img src={logo} alt="CV Carbon" className="block h-auto w-full" />
        </div>
      </div>

      <nav className="relative flex-1 space-y-5 overflow-y-auto px-4 pb-5 pt-2">
        {groups.map((group) => (
          <div key={group.label}>
            <div className="sidebar-group-label px-3 pb-1.5">{group.label}</div>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) => `sidebar-nav-item ${isActive ? "sidebar-nav-item-active" : ""}`}
                >
                  <Icon name={item.icon} className="h-[18px] w-[18px] shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="relative border-t border-white/8 px-4 py-4">
        <NavLink to="/settings" onClick={onNavigate}
          className={({ isActive }) => `sidebar-nav-item ${isActive ? "sidebar-nav-item-active" : ""}`}>
          <Icon name="settings" className="h-[18px] w-[18px]" />
          <span>Profile &amp; settings</span>
        </NavLink>

      </div>
    </>
  );
}
